import { type ReactNode, useRef } from "react";

import { cn } from "./lib/cn";
import { useDismiss } from "./lib/useOverlay";

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  /** Tetikleyici — düğmeyi çağıran sayfa çiziyor; konumlandırma buradan yapılıyor. */
  trigger: ReactNode;
  children: ReactNode;
  /** Panelin hangi kenara hizalanacağı. */
  align?: "left" | "right";
  className?: string;
}

/**
 * Tetikleyiciye bağlı açılan küçük panel (CLOS-98 `[059]`).
 *
 * ### `Modal`/`Drawer`'dan farkı: ODAK KAPANI YOK
 *
 * Popover açıkken sayfa hâlâ kullanılabilir olmalı; odağı hapsetmek klavye kullanıcısını
 * kilitlerdi. Bu yüzden yalnız `useDismiss` kullanılıyor — ESC ve dışarı tıklama kapatıyor,
 * `useFocusTrap` KULLANILMIYOR. İki kancanın ayrı olmasının sebebi tam olarak bu
 * (ADR-0022).
 *
 * `outsideClick` AÇIK: `Modal`'ın aksine örtü yok, dışarı tıklamayı dinleyen başka kimse
 * yok.
 *
 * ### Portal kullanılmıyor
 *
 * Panel tetikleyicinin yanında, akış içinde duruyor. Portal'a taşımak konumlandırmayı
 * elle hesaplamayı gerektirirdi; üst bar panelleri için `absolute` yeterli ve taşma
 * sorunu yok.
 */
export function Popover({
  open,
  onClose,
  trigger,
  children,
  align = "right",
  className,
}: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);

  /**
   * Sarmalayıcı ref: tetikleyici de KAPSAM İÇİNDE. Yalnız paneli kapsasaydı tetikleyiciye
   * tıklamak "dışarı" sayılır ve panel kapanıp hemen yeniden açılırdı.
   */
  useDismiss(ref, open, onClose, { outsideClick: true });

  return (
    <div ref={ref} className="relative">
      {trigger}
      {open ? (
        <div
          className={cn(
            "absolute z-20 mt-2 min-w-48 overflow-hidden rounded-2xl border border-border-subtle bg-surface py-1 shadow-lg",
            align === "right" ? "right-0" : "left-0",
            className,
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
